import React from 'react';
import { X, Zap, ImageIcon, Mic, MessageCircle, Repeat2, Gift, Bookmark, UserPlus, UserCheck, MoreHorizontal } from 'lucide-react';
import { timeAgo } from '../../../utils/timeAgo';
import { formatTextWithHashtags } from '../../../utils/formatText';
import LazyImage from '../../LazyImage';
import MediaViewer from '../../postcard/MediaViewer';
import { supabase } from '../../../lib/supabase';

// Extend Window interface to include gtag for Google Analytics
declare global {
  interface Window {
    gtag?: (command: string, targetId: string, config?: any) => void;
  }
}

interface Comment {
  id: string;
  content: string;
  created_at: string;
  users?: {
    username: string;
    avatar_url: string;
    verified: boolean;
  };
  content_id: string;
  impressions: number;
  updated_at: string;
  is_pinned?: boolean;
  is_ad: boolean;
}

interface User {
  username: string;
  avatar_url: string;
  verified: boolean;
}

interface Post {
  id: string;
  caption?: string;
  media_url: string;
  media_type?: 'image' | 'video' | 'music';
  additional_media_urls?: string[];
  mediaItems?: {
    id: string;
    url: string;
    type: 'image' | 'video' | 'music';
    thumbnail?: string;
  }[];
  music_info?: {
    title: string;
    artist: string;
    coverArt: string;
    duration: number;
  };
  created_at: string;
  users: User;
  likes_count: number;
  comments_count: number;
  bookmarks_count: number;
  gifts_count: number;
  views_count: number;
  comments: Comment[];
}

interface MdXxlPostViewProps {
  post: Post;
  isOpen: boolean;
  onClose?: () => void;
  initialMediaIndex?: number;
}

export default function MdXxlPostView({ post, isOpen, onClose, initialMediaIndex = 0 }: MdXxlPostViewProps) {
  const [comment, setComment] = React.useState('');
  const [comments, setComments] = React.useState<Comment[]>([
    // 30 Demo Comments
    {
      id: '1',
      content: 'This is absolutely stunning! 🔥 The composition and lighting are perfect.',
      created_at: '2024-01-15T10:30:00Z',
      users: {
        username: 'photography_pro',
        avatar_url: 'https://cdn.pixabay.com/photo/2016/11/21/12/42/beard-1845166_1280.jpg',
        verified: true
      },
      content_id: post.id,
      impressions: 89,
      updated_at: '2024-01-15T10:30:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '2',
      content: 'What camera settings did you use for this shot? The depth of field is incredible!',
      created_at: '2024-01-15T09:45:00Z',
      users: {
        username: 'camera_enthusiast',
        avatar_url: 'https://cdn.pixabay.com/photo/2017/06/26/02/47/man-2442565_1280.jpg',
        verified: false
      },
      content_id: post.id,
      impressions: 67,
      updated_at: '2024-01-15T09:45:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '3',
      content: 'Pure artistry! This belongs in a gallery 🖼️',
      created_at: '2024-01-15T09:20:00Z',
      users: {
        username: 'art_lover_2024',
        avatar_url: 'https://cdn.pixabay.com/photo/2016/11/29/09/38/adult-1868750_1280.jpg',
        verified: true
      },
      content_id: post.id,
      impressions: 124,
      updated_at: '2024-01-15T09:20:00Z',
      is_pinned: true,
      is_ad: false
    },
    {
      id: '4',
      content: 'The colors in this photo are so vibrant! How did you achieve this effect?',
      created_at: '2024-01-15T08:55:00Z',
      users: {
        username: 'color_theory_fan',
        avatar_url: 'https://cdn.pixabay.com/photo/2017/08/01/01/33/beanie-2562646_1280.jpg',
        verified: false
      },
      content_id: post.id,
      impressions: 45,
      updated_at: '2024-01-15T08:55:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '5',
      content: 'Absolutely love this! Can you share your editing process? 📸',
      created_at: '2024-01-15T08:30:00Z',
      users: {
        username: 'photo_editor_pro',
        avatar_url: 'https://cdn.pixabay.com/photo/2016/11/18/19/07/happy-1836445_1280.jpg',
        verified: true
      },
      content_id: post.id,
      impressions: 78,
      updated_at: '2024-01-15T08:30:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '6',
      content: 'This is why I love this platform! Amazing content like this inspires me every day 💯',
      created_at: '2024-01-15T08:15:00Z',
      users: {
        username: 'daily_inspiration',
        avatar_url: 'https://cdn.pixabay.com/photo/2017/04/01/21/06/portrait-2194457_1280.jpg',
        verified: false
      },
      content_id: post.id,
      impressions: 156,
      updated_at: '2024-01-15T08:15:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '7',
      content: 'Tutorial please! I need to learn this technique 🙏',
      created_at: '2024-01-15T07:45:00Z',
      users: {
        username: 'learning_photography',
        avatar_url: 'https://cdn.pixabay.com/photo/2016/11/29/20/22/girl-1871104_1280.jpg',
        verified: false
      },
      content_id: post.id,
      impressions: 92,
      updated_at: '2024-01-15T07:45:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '8',
      content: 'The lighting in this shot is phenomenal! Natural or studio?',
      created_at: '2024-01-15T07:20:00Z',
      users: {
        username: 'lighting_master',
        avatar_url: 'https://cdn.pixabay.com/photo/2017/08/06/12/06/people-2591874_1280.jpg',
        verified: true
      },
      content_id: post.id,
      impressions: 63,
      updated_at: '2024-01-15T07:20:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '9',
      content: 'This gives me major wanderlust vibes! Where was this taken? 🌍',
      created_at: '2024-01-15T06:55:00Z',
      users: {
        username: 'travel_seeker',
        avatar_url: 'https://cdn.pixabay.com/photo/2016/11/21/14/53/man-1845814_1280.jpg',
        verified: false
      },
      content_id: post.id,
      impressions: 134,
      updated_at: '2024-01-15T06:55:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '10',
      content: 'Your work keeps getting better and better! Keep it up! 🚀',
      created_at: '2024-01-15T06:30:00Z',
      users: {
        username: 'supportive_friend',
        avatar_url: 'https://cdn.pixabay.com/photo/2017/06/09/23/22/people-2388584_1280.jpg',
        verified: false
      },
      content_id: post.id,
      impressions: 87,
      updated_at: '2024-01-15T06:30:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '11',
      content: 'The detail in this image is incredible! What lens did you use?',
      created_at: '2024-01-15T06:10:00Z',
      users: {
        username: 'gear_geek',
        avatar_url: 'https://cdn.pixabay.com/photo/2016/11/29/13/14/attractive-1869761_1280.jpg',
        verified: true
      },
      content_id: post.id,
      impressions: 71,
      updated_at: '2024-01-15T06:10:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '12',
      content: 'This is going straight to my inspiration board! 📌',
      created_at: '2024-01-15T05:45:00Z',
      users: {
        username: 'creative_collector',
        avatar_url: 'https://cdn.pixabay.com/photo/2017/05/31/04/59/beautiful-2358414_1280.jpg',
        verified: false
      },
      content_id: post.id,
      impressions: 98,
      updated_at: '2024-01-15T05:45:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '13',
      content: 'The mood and atmosphere you captured here is perfect! 🌟',
      created_at: '2024-01-15T05:20:00Z',
      users: {
        username: 'mood_photographer',
        avatar_url: 'https://cdn.pixabay.com/photo/2016/11/21/12/42/beard-1845166_1280.jpg',
        verified: true
      },
      content_id: post.id,
      impressions: 115,
      updated_at: '2024-01-15T05:20:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '14',
      content: 'I could stare at this for hours. So much to discover in every corner!',
      created_at: '2024-01-15T04:55:00Z',
      users: {
        username: 'detail_observer',
        avatar_url: 'https://cdn.pixabay.com/photo/2017/04/01/21/06/portrait-2194457_1280.jpg',
        verified: false
      },
      content_id: post.id,
      impressions: 82,
      updated_at: '2024-01-15T04:55:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '15',
      content: 'This is the kind of content that makes me love photography! 📷',
      created_at: '2024-01-15T04:30:00Z',
      users: {
        username: 'photo_passion',
        avatar_url: 'https://cdn.pixabay.com/photo/2016/11/29/09/38/adult-1868750_1280.jpg',
        verified: false
      },
      content_id: post.id,
      impressions: 127,
      updated_at: '2024-01-15T04:30:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '16',
      content: 'The symmetry and balance in this composition is chef\'s kiss! 👌',
      created_at: '2024-01-15T04:05:00Z',
      users: {
        username: 'composition_critic',
        avatar_url: 'https://cdn.pixabay.com/photo/2017/08/01/01/33/beanie-2562646_1280.jpg',
        verified: true
      },
      content_id: post.id,
      impressions: 94,
      updated_at: '2024-01-15T04:05:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '17',
      content: 'Your eye for capturing the perfect moment is unmatched! ⚡',
      created_at: '2024-01-15T03:40:00Z',
      users: {
        username: 'moment_hunter',
        avatar_url: 'https://cdn.pixabay.com/photo/2016/11/18/19/07/happy-1836445_1280.jpg',
        verified: false
      },
      content_id: post.id,
      impressions: 108,
      updated_at: '2024-01-15T03:40:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '18',
      content: 'This deserves to be featured! How do I nominate this? 🏆',
      created_at: '2024-01-15T03:15:00Z',
      users: {
        username: 'feature_nominator',
        avatar_url: 'https://cdn.pixabay.com/photo/2017/06/26/02/47/man-2442565_1280.jpg',
        verified: true
      },
      content_id: post.id,
      impressions: 143,
      updated_at: '2024-01-15T03:15:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '19',
      content: 'The story this image tells is so powerful. Art at its finest! 🎨',
      created_at: '2024-01-15T02:50:00Z',
      users: {
        username: 'story_teller',
        avatar_url: 'https://cdn.pixabay.com/photo/2017/06/09/23/22/people-2388584_1280.jpg',
        verified: false
      },
      content_id: post.id,
      impressions: 76,
      updated_at: '2024-01-15T02:50:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '20',
      content: 'I\'m speechless. This is pure magic captured in a frame! ✨',
      created_at: '2024-01-15T02:25:00Z',
      users: {
        username: 'magic_seeker',
        avatar_url: 'https://cdn.pixabay.com/photo/2016/11/29/20/22/girl-1871104_1280.jpg',
        verified: true
      },
      content_id: post.id,
      impressions: 189,
      updated_at: '2024-01-15T02:25:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '21',
      content: 'The technical execution here is flawless! Respect! 🙌',
      created_at: '2024-01-15T02:00:00Z',
      users: {
        username: 'technical_reviewer',
        avatar_url: 'https://cdn.pixabay.com/photo/2017/08/06/12/06/people-2591874_1280.jpg',
        verified: false
      },
      content_id: post.id,
      impressions: 91,
      updated_at: '2024-01-15T02:00:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '22',
      content: 'This makes me want to pick up my camera right now! Inspiring! 📸',
      created_at: '2024-01-15T01:35:00Z',
      users: {
        username: 'inspired_shooter',
        avatar_url: 'https://cdn.pixabay.com/photo/2016/11/21/14/53/man-1845814_1280.jpg',
        verified: false
      },
      content_id: post.id,
      impressions: 112,
      updated_at: '2024-01-15T01:35:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '23',
      content: 'The emotion conveyed through this image is incredible! 💫',
      created_at: '2024-01-15T01:10:00Z',
      users: {
        username: 'emotion_reader',
        avatar_url: 'https://cdn.pixabay.com/photo/2016/11/29/13/14/attractive-1869761_1280.jpg',
        verified: true
      },
      content_id: post.id,
      impressions: 167,
      updated_at: '2024-01-15T01:10:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '24',
      content: 'Your portfolio must be incredible if this is just one piece! 🔥',
      created_at: '2024-01-15T00:45:00Z',
      users: {
        username: 'portfolio_admirer',
        avatar_url: 'https://cdn.pixabay.com/photo/2017/05/31/04/59/beautiful-2358414_1280.jpg',
        verified: false
      },
      content_id: post.id,
      impressions: 85,
      updated_at: '2024-01-15T00:45:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '25',
      content: 'The way you\'ve used negative space here is brilliant! 🎯',
      created_at: '2024-01-15T00:20:00Z',
      users: {
        username: 'space_analyst',
        avatar_url: 'https://cdn.pixabay.com/photo/2016/11/21/12/42/beard-1845166_1280.jpg',
        verified: true
      },
      content_id: post.id,
      impressions: 73,
      updated_at: '2024-01-15T00:20:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '26',
      content: 'This is going viral for sure! Absolutely stunning work! 🚀',
      created_at: '2024-01-14T23:55:00Z',
      users: {
        username: 'viral_predictor',
        avatar_url: 'https://cdn.pixabay.com/photo/2017/04/01/21/06/portrait-2194457_1280.jpg',
        verified: false
      },
      content_id: post.id,
      impressions: 201,
      updated_at: '2024-01-14T23:55:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '27',
      content: 'The textures and patterns in this shot are mesmerizing! 🌀',
      created_at: '2024-01-14T23:30:00Z',
      users: {
        username: 'texture_lover',
        avatar_url: 'https://cdn.pixabay.com/photo/2016/11/29/09/38/adult-1868750_1280.jpg',
        verified: false
      },
      content_id: post.id,
      impressions: 96,
      updated_at: '2024-01-14T23:30:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '28',
      content: 'I can feel the energy of this moment through the screen! ⚡',
      created_at: '2024-01-14T23:05:00Z',
      users: {
        username: 'energy_detector',
        avatar_url: 'https://cdn.pixabay.com/photo/2017/08/01/01/33/beanie-2562646_1280.jpg',
        verified: true
      },
      content_id: post.id,
      impressions: 118,
      updated_at: '2024-01-14T23:05:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '29',
      content: 'This is what I call photographic excellence! Bravo! 👏',
      created_at: '2024-01-14T22:40:00Z',
      users: {
        username: 'excellence_judge',
        avatar_url: 'https://cdn.pixabay.com/photo/2016/11/18/19/07/happy-1836445_1280.jpg',
        verified: false
      },
      content_id: post.id,
      impressions: 154,
      updated_at: '2024-01-14T22:40:00Z',
      is_pinned: false,
      is_ad: false
    },
    {
      id: '30',
      content: 'Thank you for sharing this masterpiece with us! 🙏✨',
      created_at: '2024-01-14T22:15:00Z',
      users: {
        username: 'grateful_viewer',
        avatar_url: 'https://cdn.pixabay.com/photo/2017/06/26/02/47/man-2442565_1280.jpg',
        verified: true
      },
      content_id: post.id,
      impressions: 142,
      updated_at: '2024-01-14T22:15:00Z',
      is_pinned: false,
      is_ad: false
    }
  ]);
  
  const [followingUsers, setFollowingUsers] = React.useState<Set<string>>(new Set());
  const commentsRef = React.useRef<HTMLDivElement>(null);
  const commentInputRef = React.useRef<HTMLInputElement>(null);

  // Handle media view tracking with Google Analytics
  const handleMediaView = async (mediaId: string) => {
    try {
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'media_view', {
          event_category: 'engagement',
          event_label: `post_${post.id}_media_${mediaId}`,
          value: 1
        });
      }
      // TODO: Add API call to update view counts in backend
      // await updateMediaViewCount(post.id, mediaId);
    } catch (error) {
      console.error('Error tracking media view:', error);
    }
  };

  React.useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscapeKey);
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);
  
  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim()) return;
    
    try {
      const { data, error } = await supabase
        .from('comments')
        .insert([
          { 
            content: comment, 
            content_id: post.id,
            is_pinned: false,
            is_ad: false
          }
        ]);
        
      if (error) throw error;
      
      if (data) {
        // Add the new comment to the list
        setComments([...comments, data[0]]);
        setComment('');
        
        // Scroll to the bottom of comments
        if (commentsRef.current) {
          commentsRef.current.scrollTop = commentsRef.current.scrollHeight;
        }
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  // Hide on small screens, only show on md and up
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 hidden md:flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-900 w-full h-full max-h-screen flex flex-row overflow-hidden">
        {/* Left side - Media and info */}
        <div className="flex flex-col w-3/4 h-full">
          {/* Media */}
          <div className="bg-black flex-1 flex items-center justify-center">
            <MediaViewer
              media_url={post.mediaItems?.[0]?.url || post.media_url || ''}
              caption={post.caption || ''}
              mediaType={post.media_type || 'image'}
              className="w-full h-full"
              onMediaView={handleMediaView}
              additionalMediaUrls={post.mediaItems?.slice(1).map(item => item.url) || post.additional_media_urls || []}
              musicInfo={post.music_info}
              initialMediaIndex={initialMediaIndex}
              postId={post.id}
              postOwnerId={post.users.id}
            />
          </div>
        </div>
        
        {/* Right side - Comments and input */}
        <div className="flex flex-col w-1/4 h-full border-l">
          {/* Header with X button and User Info */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center space-x-3">
              <LazyImage
                src={post.users.avatar_url}
                alt={post.users.username}
                className="w-10 h-10 rounded-full"
              />
              <div>
                <h3 className="font-semibold">{post.users.username}</h3>
                <p className="text-sm text-gray-500">{timeAgo(post.created_at)}</p>
              </div>
            </div>
            <button
              onClick={() => onClose?.()}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Caption */}
          {post.caption && (
            <div className="p-6 border-b">
              <p className="text-gray-900 dark:text-gray-100">
                {formatTextWithHashtags(post.caption)}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <button className="flex items-center space-x-1 text-gray-600 hover:text-[#065900] transition-colors">
                <Zap className="w-5 h-5" />
              </button>
              <button className="flex items-center space-x-1 text-gray-600 hover:text-[#065900] transition-colors">
                <MessageCircle className="w-5 h-5" />
                <span className="text-sm font-medium">{comments.length}</span>
              </button>
              <button className="flex items-center space-x-1 text-gray-600 hover:text-[#065900] transition-colors">
                <Repeat2 className="w-5 h-5" />
              </button>
            </div>

            {/* Centered username with plus icon */}
            <div className="flex-1 flex justify-center">
              <button className="flex items-center space-x-1 px-3 py-1.5 rounded-md bg-[#065900] text-white hover:bg-[#054700] transition-colors">
                <UserPlus className="w-4 h-4" />
                <span className="text-sm font-medium">{post.users.username}</span>
              </button>
            </div>

            <div className="flex items-center space-x-6">
              <button className="flex items-center space-x-1 text-gray-600 hover:text-[#065900] transition-colors">
                <Gift className="w-5 h-5" />
                <span className="text-sm font-medium">{post.gifts_count}</span>
              </button>
              <button className="flex items-center space-x-1 text-gray-600 hover:text-[#065900] transition-colors">
                <Bookmark className="w-5 h-5" />
                <span className="text-sm font-medium">{post.bookmarks_count}</span>
              </button>
            </div>
          </div>

          {/* Comments */}
          <div className="flex-1 overflow-y-scroll scrollbar-hide scroll-smooth" ref={commentsRef}>
            <div className="p-4 space-y-4">
              {/* Pinned comments first */}
              {comments
                .filter(comment => comment.is_pinned)
                .map((comment) => (
                  <div 
                    key={comment.id} 
                    className="flex space-x-3 p-3 rounded-lg transition-all duration-300 hover:bg-gray-50 dark:hover:bg-gray-800/50 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400"
                  >
                    <LazyImage
                      src={comment.users?.avatar_url || ''}
                      alt={comment.users?.username || ''}
                      className="w-8 h-8 rounded-full flex-shrink-0 ring-2 ring-gray-200 dark:ring-gray-700"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                          {comment.users?.username}
                        </span>
                        {comment.users?.verified && (
                          <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                        <span className="text-xs bg-yellow-400 text-black px-2 py-0.5 rounded">📌 Pinned</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {timeAgo(comment.created_at)}
                        </span>
                        <div className="ml-auto">
                          <button 
                            onClick={() => {
                              const newFollowing = new Set(followingUsers);
                              if (followingUsers.has(comment.users?.username || '')) {
                                newFollowing.delete(comment.users?.username || '');
                              } else {
                                newFollowing.add(comment.users?.username || '');
                              }
                              setFollowingUsers(newFollowing);
                            }}
                            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                          >
                            {followingUsers.has(comment.users?.username || '') ? (
                              <UserCheck className="w-4 h-4 text-[#ffc11f]" />
                            ) : (
                              <UserPlus className="w-4 h-4 text-white" />
                            )}
                          </button>
                        </div>
                      </div>
                      <p className="text-sm text-gray-900 dark:text-gray-100 mt-1 leading-relaxed">
                        {formatTextWithHashtags(comment.content)}
                      </p>
                      <div className="flex items-center space-x-4 mt-2">
                        <button className="flex items-center space-x-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                          <Zap className="w-3 h-3" />
                          <span>{comment.impressions || 0}</span>
                        </button>
                        <button className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                          Reply
                        </button>
                        <button className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                          Replies {Math.floor(Math.random() * 10)}
                        </button>
                        <button className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                          <MoreHorizontal className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                
              {/* Advertisement below pinned comment */}
              {comments.some(comment => comment.is_pinned) && (
                <div className="flex space-x-3 p-4 rounded-lg bg-gradient-to-r from-[#065900] to-[#076a00] text-white">
                  <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 bg-yellow-400 rounded-full">
                    <Zap className="w-6 h-6 text-black" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-semibold">Boost Your Profile</span>
                      <span className="text-xs bg-yellow-400 text-black px-2 py-0.5 rounded">Sponsored</span>
                    </div>
                    <p className="text-sm mt-1">Get 5x more visibility on your posts with MoZo Premium!</p>
                    <div className="mt-3 flex space-x-2">
                      <button className="bg-yellow-400 hover:bg-yellow-500 text-black px-3 py-1 rounded-md text-sm font-medium transition-colors">
                        Learn More
                      </button>
                      <button className="bg-black/20 hover:bg-black/30 text-white px-3 py-1 rounded-md text-sm transition-colors">
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Regular comments */}
              {comments
                .filter(comment => !comment.is_pinned)
                .map((comment) => (
                  <div 
                    key={comment.id} 
                    className="flex space-x-3 p-3 rounded-lg transition-all duration-300 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  >
                    <LazyImage
                      src={comment.users?.avatar_url || ''}
                      alt={comment.users?.username || ''}
                      className="w-8 h-8 rounded-full flex-shrink-0 ring-2 ring-gray-200 dark:ring-gray-700"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                          {comment.users?.username}
                        </span>
                        {comment.users?.verified && (
                          <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {timeAgo(comment.created_at)}
                        </span>
                        <div className="ml-auto">
                          <button 
                            onClick={() => {
                              const newFollowing = new Set(followingUsers);
                              if (followingUsers.has(comment.users?.username || '')) {
                                newFollowing.delete(comment.users?.username || '');
                              } else {
                                newFollowing.add(comment.users?.username || '');
                              }
                              setFollowingUsers(newFollowing);
                            }}
                            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                          >
                            {followingUsers.has(comment.users?.username || '') ? (
                              <UserCheck className="w-4 h-4 text-[#ffc11f]" />
                            ) : (
                              <UserPlus className="w-4 h-4 text-white" />
                            )}
                          </button>
                        </div>
                      </div>
                      <p className="text-sm text-gray-900 dark:text-gray-100 mt-1 leading-relaxed">
                        {formatTextWithHashtags(comment.content)}
                      </p>
                      <div className="flex items-center space-x-4 mt-2">
                        <button className="flex items-center space-x-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                          <Zap className="w-3 h-3" />
                          <span>{comment.impressions || 0}</span>
                        </button>
                        <button className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                          Reply
                        </button>
                        <button className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                          Replies {Math.floor(Math.random() * 10)}
                        </button>
                        <button className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                          <MoreHorizontal className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Comment Input */}
          <div className="p-4 border-t bg-white dark:bg-gray-900">
            <form onSubmit={handleSubmitComment} className="flex items-center space-x-2">
              <LazyImage
                src={post.users.avatar_url}
                alt="Your avatar"
                className="w-8 h-8 rounded-full"
              />
              <div className="flex-1 flex items-center bg-gray-100 dark:bg-gray-800 rounded-full px-4 py-2 relative">
                <button type="button" className="text-gray-400 hover:text-gray-600 mr-2">
                  <ImageIcon className="w-5 h-5" />
                </button>
                <button type="button" className="text-gray-400 hover:text-gray-600 mr-2">
                  <Mic className="w-5 h-5" />
                </button>
                <input
                  ref={commentInputRef}
                  type="text"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add a comment..."
                  className="flex-1 bg-transparent outline-none text-sm placeholder-gray-500"
                />
              </div>
              <button
                type="submit"
                disabled={!comment.trim()}  
                className="bg-[#065900] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#054700] transition-colors disabled:opacity-50"
              >
                Send
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
